/**
 * Telehealth Redux Slice
 *
 * Manages UI state for video conferencing.
 * Session data comes from API, but UI state (device selection, call controls) lives here.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { ChatEntry, EncounterMode, TelehealthSession, TelehealthState } from '../types';

const initialState: TelehealthState = {
  activeSession: null,
  deviceSettings: {
    selectedCameraId: null,
    selectedSpeakerId: null,
    noiseCancellationEnabled: false,
    backgroundBlurMode: 'none',
    hideSelfView: false,
  },
  preCallSettings: {
    isInPreCall: false,
    isCameraOn: true,
    isMicOn: true,
    userName: '',
  },
  callPanelRect: null,
  chatMessages: [],
  chatUnreadCount: 0,
  isChatPanelOpen: false,
  encounterMode: 'standard',
};

export const telehealthSlice = createSlice({
  name: 'telehealth',
  initialState,
  reducers: {
    setActiveSession: (state, action: PayloadAction<TelehealthSession>) => {
      state.activeSession = action.payload;
    },

    clearActiveSession: (state) => {
      state.activeSession = null;
    },

    setSelectedCamera: (state, action: PayloadAction<string>) => {
      state.deviceSettings.selectedCameraId = action.payload;
    },

    setSelectedSpeaker: (state, action: PayloadAction<string>) => {
      state.deviceSettings.selectedSpeakerId = action.payload;
    },

    setNoiseCancellation: (state, action: PayloadAction<boolean>) => {
      state.deviceSettings.noiseCancellationEnabled = action.payload;
    },

    setBackgroundBlurMode: (state, action: PayloadAction<'none' | 'light' | 'strong'>) => {
      state.deviceSettings.backgroundBlurMode = action.payload;
    },

    setHideSelfView: (state, action: PayloadAction<boolean>) => {
      state.deviceSettings.hideSelfView = action.payload;
    },

    enterPreCall: (state) => {
      state.preCallSettings.isInPreCall = true;
    },

    exitPreCall: (state) => {
      state.preCallSettings.isInPreCall = false;
    },

    setPreCallCamera: (state, action: PayloadAction<boolean>) => {
      state.preCallSettings.isCameraOn = action.payload;
    },

    setPreCallMic: (state, action: PayloadAction<boolean>) => {
      state.preCallSettings.isMicOn = action.payload;
    },

    setUserName: (state, action: PayloadAction<string>) => {
      state.preCallSettings.userName = action.payload;
    },

    setCallPanelRect: (
      state,
      action: PayloadAction<{ width: number; height: number; x: number; y: number }>,
    ) => {
      state.callPanelRect = action.payload;
    },

    // Chat
    addChatMessage: (state, action: PayloadAction<ChatEntry>) => {
      state.chatMessages.push(action.payload);
      if (
        !state.isChatPanelOpen &&
        action.payload.type === 'chat' &&
        action.payload.senderRole !== 'provider'
      ) {
        state.chatUnreadCount += 1;
      }
    },

    clearChatMessages: (state) => {
      state.chatMessages = [];
      state.chatUnreadCount = 0;
    },

    setChatPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.isChatPanelOpen = action.payload;
      if (action.payload) {
        state.chatUnreadCount = 0;
      }
    },

    setEncounterMode: (state, action: PayloadAction<EncounterMode>) => {
      state.encounterMode = action.payload;
    },

    toggleChatPanel: (state) => {
      state.isChatPanelOpen = !state.isChatPanelOpen;
      if (state.isChatPanelOpen) {
        state.chatUnreadCount = 0;
      }
    },
  },
});

export const {
  setActiveSession,
  clearActiveSession,
  setSelectedCamera,
  setSelectedSpeaker,
  setNoiseCancellation,
  setBackgroundBlurMode,
  setHideSelfView,
  enterPreCall,
  exitPreCall,
  setPreCallCamera,
  setPreCallMic,
  setUserName,
  setCallPanelRect,
  addChatMessage,
  clearChatMessages,
  setChatPanelOpen,
  setEncounterMode,
  toggleChatPanel,
} = telehealthSlice.actions;

export default telehealthSlice.reducer;
